import { Box, IconButton, TableCell, TableRow } from "@mui/material";
import DialogComponent from "../../../common/components/functional/dialogs/DialogComponent";
import CloseIcon from "@mui/icons-material/Close";
import { useCallback, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { fetchCoreUsers, fetchTalentLog } from "../../talents/talentsSlice";
import { StripedTable } from "../../../common/components/functional/stripedTable/StripedTable";
import { isFulfilled } from "@reduxjs/toolkit";
import { format } from "date-fns";
import LoadingSpinner from "../../../common/components/functional/loadingSpinner/LoadingSpinner";
import {
  dateTimeSecondsFormat,
  leadingZerosDateFormat,
} from "../../../common/helpers/formatDateTime";
import { useTranslation } from "react-i18next";
import isEmpty from "../../../common/helpers/isEmpty";
import {
  fetchExpertUsers,
  selectAllExpertUsers,
} from "../../user/expertUsersSlice";
import { RootState } from "../../../app/store";
import { selectLanguageSkillsCodeNameIndex } from "../../languageSkills/languageSkillsSlice";
import { TalentLog } from "../../talents/talentAPI";
import { StripedTableHeader } from "../../../common/components/functional/stripedTable/types";
import DialogContentWrapper from "../../../common/components/functional/dialogs/DialogContentWrapper";
import { getCoreUserFullName } from "../../../common/utils/users";

type Props = {
  talentId: string;
  talentName: string;
  open: boolean;
  onClose: () => void;
};

export const TalentChangeLog = ({
  talentId,
  talentName,
  open,
  onClose,
}: Props) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<TalentLog[]>([]);
  const expertUsers = useAppSelector((state) => selectAllExpertUsers(state));

  const workTitles = useAppSelector(
    (state: RootState) => state.localized?.workTitle,
  );
  const languageSkills = useAppSelector((state) =>
    selectLanguageSkillsCodeNameIndex(state),
  );
  const tes = useAppSelector((state: RootState) => state.localized?.tes);

  const handleDescription = useCallback(
    (eventName: string, payload: any) => {
      switch (eventName) {
        case "idn-talentAccount-registered":
          return t("talentProfile:changeLog.talentRegistered");
        case "idn-talent-tos-approved":
          return t("talentProfile:changeLog.tosApproved");
        case "idn-talentAccount-statusUpdated":
          return `${t(`talentProfile:changeLog.${payload.status}`)}`;
        case "empAttrSal":
          return `${t("talentProfile:changeLog.salaryUpdated", {
            workTitle: workTitles[payload.workTitleId],
            tes: tes[payload.tesId],
            date: format(payload.from, leadingZerosDateFormat),
          })}`;
        case "empAttrEnd":
          return `${t("talentProfile:changeLog.salaryEnded", {
            workTitle: workTitles[payload.workTitleId],
            tes: tes[payload.tesId],
            date: format(payload.from, leadingZerosDateFormat),
          })}`;
        case "tal-wrktitles-upd":
        case "my-wrktitles-upd":
          const workTitlesLocalized = payload.workTitles
            .map((wt: string) => workTitles[wt])
            .join(" / ");
          return `${t("talentProfile:changeLog.workTitlesUpdated")} ${workTitlesLocalized}`;
        case "tal-lngs-upd":
        case "my-langs-upd":
          const languageSkillsLocalized = payload.languages
            .map(
              (lang: { languageCode: string; skillLevel: string }) =>
                `${languageSkills[lang.languageCode]}: ${t(`languageSkill:${lang.skillLevel}`)}`,
            )
            .join(" / ");
          return `${t("talentProfile:changeLog.languageSkillsUpdated")} ${languageSkillsLocalized}`;
        default:
          return eventName;
      }
    },
    [t, workTitles, tes, languageSkills],
  );

  useEffect(() => {
    if (!isEmpty(expertUsers)) return;
    dispatch(fetchExpertUsers({}));
  }, [dispatch, expertUsers]);

  // Fetch only once per page load even on dialog reopen
  useEffect(() => {
    if (!open || !talentId || !isEmpty(data)) return;
    setLoading(true);
    dispatch(fetchTalentLog({ talentId })).then((logAction) => {
      if (isFulfilled(logAction)) {
        const userIds = Array.from(
          new Set(logAction.payload.map((log) => log.userId)),
        );
        dispatch(fetchCoreUsers({ userIds })).then((usersAction) => {
          if (isFulfilled(usersAction)) {
            const userPayload = usersAction.payload.data;
            let usersList: { id: string; name: string; type: string }[] = [
              { id: talentId, name: talentName, type: "Talent" },
            ];
            usersList.push(
              ...userPayload.map((user) => ({
                id: user.id,
                name: getCoreUserFullName(user),
                type: user.type,
              })),
            );
            const logs = logAction.payload.map((log) => {
              const user = usersList.find((user) => user.id === log.userId);
              return {
                ...log,
                userName: user?.name || log.userId,
                userType: user?.type || "",
              };
            });
            setData(logs);
          }
        });
      }
      setLoading(false);
    });
  }, [
    open,
    talentId,
    dispatch,
    data,
    expertUsers,
    talentName,
    handleDescription,
  ]);

  const headers: StripedTableHeader[] = [
    {
      key: "date",
      label: t("common:date"),
      width: "15%",
    },
    {
      key: "userName",
      label: t("common:user"),
      width: "25%",
    },
    {
      key: "description",
      label: t("common:description"),
      width: "60%",
    },
  ];

  const rowLoading = (
    <TableRow key="loading">
      <TableCell colSpan={3}>
        <LoadingSpinner />
      </TableCell>
    </TableRow>
  );

  const rows = data.map((log, i) => {
    const description = handleDescription(log.name, log.payload);
    return (
      <TableRow key={i}>
        <TableCell>
          {format(new Date(log.time), dateTimeSecondsFormat)}
        </TableCell>
        <TableCell>
          {log.userName} {log.userType && `(${t(`common:${log.userType}`)})`}
        </TableCell>
        <TableCell>{description}</TableCell>
      </TableRow>
    );
  });

  return (
    <DialogComponent
      open={open}
      onClose={onClose}
      sx={{
        minWidth: "900px",
      }}
    >
      <DialogContentWrapper
        title={t("talentProfile:changeLog.changeLog")}
        draggable={false}
        childContainerSx={{ paddingTop: 2, marginLeft: "-8px" }}
      >
        <Box
          display="flex"
          justifyContent="flex-end"
          width="100%"
          sx={{ position: "absolute", top: 0, right: 0 }}
        >
          <IconButton onClick={onClose} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <StripedTable
          columnHeaders={headers}
          itemRows={loading ? [rowLoading] : rows}
        />
      </DialogContentWrapper>
    </DialogComponent>
  );
};
